import { TableIds } from "../../../common/table-identifier-utils"
import {
    checkboxColDefCreator,
    costCodeColDefCreator,
    dateColDefCreator,
    editableTimeCardColDefCreator,
    employeeColDefCreator,
    hoursFromMinutesColDefCreator,
    hoursMinutesColDefCreator,
    lengthLimitedColDefCreator,
    signatureActionsColDefCreator,
    timekeepingStatusColDefCreator,
} from "./standard-col-def-creators"
import { workShiftDateDefaultTimeValueSetter } from "../../../common/ag-grid-value-setters"
import { alertCellClassRule, getAvailableStatusValues, isGroupRow, timekeepingDataIsEditable, timekeepingRowIsEditable } from "../../../common/ag-grid-utils"
import GroupRowFormattingDefaultCellRenderer from "../cell-renderers/GroupRowFormattingDefaultCellRenderer"
import TKEntryFieldCellRenderer from "../cell-renderers/TKEntryFieldCellRenderer"
import { createModifierColDefs } from "../../../tk-modifiers/utils"
import { totalHoursValueGetter } from "../../../common/ag-grid-value-getters"
import { timekeepingCommentTooltip, timekeepingEntryGeneralErrorsTooltip, timekeepingEntrySignatureActionsTooltip } from "../../../common/ag-grid-custom-tooltip-formatters"
import { getFlagEnabled } from "../../../getFlagValue"
import AttachmentsRenderer from "../cell-renderers/attachments-cell-renderer"
import { compareGroupColumns } from "../../../common/ag-grid-comparators"
import { validateTimekeepingEntries } from "../../../common/validators"
import { getObjectId, getProjectsAndWorkShiftsFromTKRow, lockedPeriodCellClassRule } from "../../../common/ag-grid-ts-utils"
import { getSelectorActionButtons } from "./action-button-settings"
import { referenceablesToValueFormatters } from "../../../common/referenceable-value-formatters"
import { toggleNonExceptionRowsVisible, togglePlaceholdersVisible } from "../../../actions"
import Rmbx from "../../../util"
import CostCodeDetailCellRenderer from "../cell-renderers/cost-code-detail-cell-renderer"
import { activateException } from "../custom-filters/utils"
import { toggleException } from "../../../actions/exceptions"
import { addEmployeeRowsTableAction, addRowTableAction, viewCicoTableButton, viewCicoForSelectedRowsButton, editTimecardDetailsAction } from "./common-button-defs"

const timekeepingEntriesDefaultColDef = () => {
    const defaultColDef = {
        cellClassRules: {
            "period-locked": lockedPeriodCellClassRule,
            readonly: params => !timekeepingDataIsEditable(params).canEdit,
            "alert-cell": alertCellClassRule,
        },
        editable: params => timekeepingDataIsEditable(params).canEdit,
        filter: false,
        customTooltip: timekeepingEntryGeneralErrorsTooltip,
        warningsAndErrorsTooltip: getFlagEnabled("WA-8985-work-rules"),
    }
    defaultColDef.parentContainerId = "weekly-tk-detail-table-modal"
    return defaultColDef
}

export const getTimekeepingEntryTable = (featureFlags, _currentProject, user, _currentProjectIds, sourceData, equipmentRequired = false) => {
    const userRole = user ? user.user_role : undefined
    const features = user ? user.features : {}
    const groupByTableAction = {
        label: "Group By",
        action: "groupBy",
        icon: "group",
    }
    const exceptionFilters = featureFlags.cico ? ["cicoDiffException"] : []
    if (getFlagEnabled("WA-8985-work-rules")) {
        exceptionFilters.push("workRulesException")
    } else {
        exceptionFilters.push("hoursException")
    }
    return {
        tableName: "Timekeeping",
        navId: "timekeepingEntriesTable",
        id: TableIds.TimekeepingModal,
        resources: ["timekeepingEntries"],
        colDefs: [
            {
                ...dateColDefCreator({}),
                valueSetter: workShiftDateDefaultTimeValueSetter,
            },
            {
                ...checkboxColDefCreator({}),
                checkboxSelection: p => !isGroupRow(p),
            },
            {
                ...employeeColDefCreator({
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    includeCico: featureFlags.cico,
                    isDesignSystem: featureFlags.cico,
                }),
                extraFilterQueryParams: ["cico_start_date", "cico_end_date"],
                ignoreRelatedFilters: ["projectId"],
            },
            {
                ...costCodeColDefCreator({
                    cellRenderer: CostCodeDetailCellRenderer,
                    titleFormatter: referenceablesToValueFormatters.costCodes.titleAndStatusFormatter,
                }),
                filterValueGetter: params => {
                    if (getFlagEnabled("WA-9197-pto-accruals") && params.data?.pto_bucket_id != null) {
                        // Filter cost codes by PTO bucket ID when specified in the row data.
                        params = {
                            ...params,
                            filters: { ...params.filters, pto_bucket_id: params.data.pto_bucket_id },
                        }
                    } else if (getFlagEnabled("WA-9197-pto-accruals") && params.data?.has_pto_bucket === true) {
                        // Filter to only cost codes that belong to any PTO bucket.
                        params = {
                            ...params,
                            filters: { ...params.filters, has_pto_bucket: "true" },
                        }
                    }
                    const cookieVal = Rmbx.store.get("filter_ccs_by_ws")
                    const { project_id: projectFilters, work_shift_id: workShiftFilters } = params.filters
                    const projectFilterSet = projectFilters?.length ? new Set(Array.isArray(projectFilters) ? projectFilters : [projectFilters]) : new Set()
                    const workShiftFilterSet = workShiftFilters?.length ? new Set(Array.isArray(workShiftFilters) ? workShiftFilters : [workShiftFilters]) : new Set()

                    if (cookieVal === false || (cookieVal === undefined && !featureFlags?.large_project_count_optimizations)) return params.filters
                    if (getFlagEnabled("WA-8715-add-cost-code-optimization")) {
                        const filterVals = getProjectsAndWorkShiftsFromTKRow(params.data, params.context, workShiftFilterSet, projectFilterSet)
                        return {
                            ...params.filters,
                            project_id: Array.from(filterVals.projectFilterSet),
                            work_shift_id: Array.from(filterVals.workShiftFilterSet),
                        }
                    } else {
                        if (!params.data.work_shift_id) {
                            const workShiftLockedColumn = params.context.groupKeyInfo.find(col => col.colDef.field === "/work_shift_id")
                            if (workShiftLockedColumn) {
                                const workShiftId = getObjectId(workShiftLockedColumn.value)
                                if (workShiftFilterSet?.size)
                                    return {
                                        ...params.filters,
                                        work_shift_id: Array.from(workShiftFilterSet.intersection(new Set([workShiftId]))),
                                    }
                                return { ...params.filters, work_shift_id: workShiftId }
                            }
                            const projectLockedColumn = params.context.groupKeyInfo.find(col => col.colDef.field === "/project")
                            if (projectLockedColumn) {
                                const projectId = getObjectId(projectLockedColumn.value)
                                return {
                                    ...params.filters,
                                    project_id: projectFilterSet?.size ? Array.from(projectFilterSet.intersection(new Set([projectId]))) : projectId,
                                }
                            }
                            return params.filters
                        }
                        if (typeof params.data.work_shift_id === "object") {
                            const projectIds = params.data.work_shift_id.projects.map(p => getObjectId(p))
                            return {
                                ...params.filters,
                                project_id: projectFilterSet?.size ? Array.from(projectFilterSet.intersection(new Set(projectIds))) : projectIds,
                            }
                        }
                        if (params.context.referenceableData?.workShifts[params.data.work_shift_id]) {
                            const projectIds = params.context.referenceableData?.workShifts[params.data.work_shift_id].projects.map(p => getObjectId(p))

                            return {
                                ...params.filters,
                                project_id: projectFilterSet?.size ? Array.from(projectFilterSet.intersection(new Set(projectIds))) : projectIds,
                            }
                        }
                        // Let's just send along the work shift id
                        return {
                            ...params.filters,
                            work_shift_id: workShiftFilterSet ? Array.from(workShiftFilterSet.intersection(new Set([params.data.work_shift_id]))) : params.data.work_shift_id,
                        }
                    }
                },
            },
            {
                ...editableTimeCardColDefCreator({
                    headerName: "Time Card",
                    required: true,
                    width: 300,
                    features: featureFlags,
                }),
                ignoreRelatedFilters: [],
                extraFilterQueryParams: ["start_date", "foreman_id", "project"],
                extraSearchFilters: { allow_empty: true },
                actionOptions: [getSelectorActionButtons().createNewTimecardButton],
            },
            ...(featureFlags.cost_code_level_modifiers ? createModifierColDefs(user.company_tk_modifiers ?? [], sourceData?.sourceData, timekeepingEntriesDefaultColDef(), equipmentRequired) : []),
            {
                ...hoursMinutesColDefCreator({
                    headerName: "ST",
                    field: "/adjusted_minutes_st",
                    cellRenderer: getFlagEnabled("WA-9079-wage-rates") ? TKEntryFieldCellRenderer : GroupRowFormattingDefaultCellRenderer,
                }),
                ...(getFlagEnabled("WA-9079-wage-rates")
                    ? {
                          cellRendererParams: {
                              timeType: "ST",
                          },
                      }
                    : {}),
                aggFunc: "sum",
            },
            {
                ...hoursMinutesColDefCreator({
                    headerName: "OT",
                    field: "/adjusted_minutes_ot",
                    cellRenderer: getFlagEnabled("WA-9079-wage-rates") ? TKEntryFieldCellRenderer : GroupRowFormattingDefaultCellRenderer,
                }),
                ...(getFlagEnabled("WA-9079-wage-rates")
                    ? {
                          cellRendererParams: {
                              timeType: "OT",
                          },
                      }
                    : {}),
                aggFunc: "sum",
            },
            {
                ...hoursMinutesColDefCreator({
                    headerName: "DT",
                    field: "/adjusted_minutes_dt",
                    cellRenderer: getFlagEnabled("WA-9079-wage-rates") ? TKEntryFieldCellRenderer : GroupRowFormattingDefaultCellRenderer,
                }),
                ...(getFlagEnabled("WA-9079-wage-rates")
                    ? {
                          cellRendererParams: {
                              timeType: "DT",
                          },
                      }
                    : {}),
                aggFunc: "sum",
            },
            {
                ...hoursFromMinutesColDefCreator({
                    headerName: "Total",
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                }),
                valueGetter: totalHoursValueGetter,
                aggFunc: "sum",
            },
            ...(featureFlags.timekeeping_comments
                ? [
                      {
                          ...lengthLimitedColDefCreator({
                              headerName: "Comment",
                              field: "/comment",
                              maxLength: user.company_options.tkcomment_max_length,
                          }),
                          customTooltip: timekeepingCommentTooltip,
                      },
                  ]
                : []),
            {
                ...timekeepingStatusColDefCreator({
                    enum: getAvailableStatusValues(userRole),
                }),
                cellRenderer: GroupRowFormattingDefaultCellRenderer,
                aggFunc: "statusColumnAggFunc",
            },
            {
                headerName: "Signature",
                field: "/signature",
                width: 110,
                editable: false,
                cellRenderer: AttachmentsRenderer("Signature"),
                sortable: false,
                suppressSizeToFit: true,
            },

            ...(!features?.disable_web_signature_flow
                ? [
                      {
                          ...signatureActionsColDefCreator({
                              headerName: "Actions",
                              field: "/signature",
                              shouldPreventActionFocus: true,
                          }),
                          customTooltip: timekeepingEntrySignatureActionsTooltip,
                      },
                  ]
                : []),
        ],
        gridSettings: {
            defaultColDef: timekeepingEntriesDefaultColDef(),
            autoGroupColumnDef: {
                headerName: "Employee",
                suppressMenu: true,
                sort: "asc",
                unSortIcon: true,
                cellRendererParams: {
                    suppressCount: true,
                },
                comparator: compareGroupColumns,
                checkboxSelection: p => isGroupRow(p),
                groupSelectsChildren: false,
                minWidth: 250,
            },
            enableRangeSelection: true,
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            suppressMultiRangeSelection: true,
            isRowSelectable: rowNode => timekeepingRowIsEditable(rowNode),
        },
        otherSettings: {
            enableClipboard: true,
            pasteRangeEnabled: true,
            filterByLockedColumns: true,
            highlightErrorRows: true,
            hideLockedColumns: true,
            hideCheckboxInHistory: true,
            sizeColumnsToFitSize: 915,
            isInModalTab: true,
            rowLevelValidators: [validateTimekeepingEntries],
            rowLevelErrorDisplay: true,
            suppressExportButton: true,
            exceptionFilters: exceptionFilters,
            // Although the settings specifies possible groupBy options for the
            // Timekeeping tab of the modal, these options are configured on the fly based on
            // the grouping that's in place on the main table when you open the modal.
            // That configuration is handled in the groupingAllowedInCurrentConfiguration function
            // in toolbar/event-handlers/utils.ts
            groupBy: [
                {
                    cols: [],
                    label: "None",
                },
                {
                    cols: ["/employee"],
                    label: "Employee",
                },
                {
                    cols: ["/cost_code"],
                    label: "Cost Code",
                },
            ],
            buttons: {
                cell: [],
                row: [
                    {
                        label: "Delete",
                        icon: "delete",
                        action: "deleteSelectedRows",
                        args: {
                            local_state_update: true,
                        },
                    },
                    editTimecardDetailsAction,
                    ...(getFlagEnabled("WA-9079-wage-rates") && featureFlags.rate_tables && userRole === "ADMIN"
                        ? [
                              {
                                  label: "Rates",
                                  action: "getLaborRateActions",
                                  icon: "costCode",
                                  ungroupOptions: false,
                                  dropdown: true,
                                  args: {
                                      local_state_update: true,
                                  },
                              },
                          ]
                        : []),
                    ...(featureFlags.cico ? [viewCicoForSelectedRowsButton] : []),
                ],
                groupRow: [
                    {
                        label: "Add Row",
                        icon: "add",
                        action: "addNewRowToGroup",
                    },
                ],
                table: [
                    groupByTableAction,
                    addRowTableAction(featureFlags),
                    ...(getFlagEnabled("WA-9197-pto-accruals")
                        ? [
                              {
                                  label: "Add PTO",
                                  icon: "addPTO",
                                  action: "addNewRow",
                                  args: { extraArgs: { initialRowData: { has_pto_bucket: true } } },
                              },
                          ]
                        : []),
                    addEmployeeRowsTableAction,

                    {
                        label: "Add Cost Codes",
                        action: "addCostCodeRows",
                        icon: "costCode",
                        disabled: context => context.groupKeyInfo?.find(key => key.colDef.field === "/cost_code"),
                        tooltip: context =>
                            context.groupKeyInfo?.find(key => key.colDef.field === "/cost_code")
                                ? "The entries you are viewing are for a specific " + "cost code. New cost codes cannot be added to this view"
                                : undefined,
                    },
                    ...(!featureFlags.cico
                        ? [
                              {
                                  label: "Show Placeholders",
                                  action: "togglePlaceholders",
                                  icon: "view",
                                  customButtonType: "placeholders",
                              },
                          ]
                        : []),
                    editTimecardDetailsAction,
                    ...(featureFlags.cico
                        ? [
                              {
                                  label: "Show/Hide ▾",
                                  icon: "view",
                                  args: {
                                      extraArgs: {
                                          options: [
                                              {
                                                  label: "Placeholders",
                                                  action: togglePlaceholdersVisible,
                                                  // The state parameter we look at to see if this should be
                                                  // checked on
                                                  activeStateParams: ["placeholdersVisible"],
                                              },
                                              {
                                                  label: "Non-exception Rows",
                                                  action: toggleNonExceptionRowsVisible,
                                                  activeStateParams: ["nonExceptionRowsVisible"],
                                              },
                                          ],
                                      },
                                  },
                                  checklist: true,
                              },
                          ]
                        : []),
                    ...(featureFlags.cico ? [viewCicoTableButton] : []),
                    ...[
                        {
                            label: "Exceptions ▾",
                            icon: "exception",
                            args: {
                                extraArgs: {
                                    options: [
                                        {
                                            label: "Exceptions",
                                            action: () => dispatch => {
                                                if (featureFlags.cico) {
                                                    dispatch(toggleException("cicoDiffException"))
                                                }
                                                if (getFlagEnabled("WA-8985-work-rules")) {
                                                    dispatch(toggleException("workRulesException"))
                                                } else {
                                                    dispatch(toggleException("hoursException"))
                                                }
                                            },
                                            exceptionParams: getFlagEnabled("WA-8985-work-rules")
                                                ? featureFlags.cico
                                                    ? ["cicoDiffException", "workRulesException"]
                                                    : ["workRulesException"]
                                                : featureFlags.cico
                                                ? ["cicoDiffException", "hoursException"]
                                                : ["hoursException"],
                                            hasGear: false,
                                        },
                                        ...(featureFlags.cico
                                            ? [
                                                  {
                                                      label: "CI/CO",
                                                      action: activateException(() => toggleException("cicoDiffException")),
                                                      exceptionParams: ["cicoDiffException"],
                                                      hasGear: true,
                                                      gearModalType: "cicoException",
                                                      isChild: true,
                                                  },
                                              ]
                                            : []),
                                        ...(getFlagEnabled("WA-8985-work-rules")
                                            ? [
                                                  {
                                                      label: "Work Rules",
                                                      action: activateException(() => toggleException("workRulesException")),
                                                      exceptionParams: ["workRulesException"],
                                                      hasGear: true,
                                                      gearModalType: "workRulesException",
                                                      isChild: true,
                                                  },
                                              ]
                                            : [
                                                  {
                                                      label: "Irregular Hours",
                                                      action: activateException(() => toggleException("hoursException")),
                                                      exceptionParams: ["hoursException"],
                                                      hasGear: true,
                                                      gearModalType: "hoursException",
                                                      isChild: true,
                                                  },
                                              ]),
                                    ],
                                    exceptionList: true,
                                },
                            },
                            checklist: true,
                        },
                    ],
                    {
                        label: "Project Cost Codes Only",
                        checkbox: true,
                        args: {
                            extraArgs: {
                                field: "filter_ccs_by_ws",
                                testId: "project-cost-codes-only-checkbox",
                            },
                        },
                        tooltip: "Filters cost codes down to just the projects present on the row's time card",
                    },
                ],
            },
        },
    }
}
